import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, MessageCircle, ShieldCheck, FileText, Users, MapPin, CalendarDays, Layers, Percent } from "lucide-react";
import { post } from "../lib/api";
import { leadSourceFields } from "../lib/leadSource";
import { measureLead } from "../lib/openaiPixel";
import { Eyebrow, Reveal, MaskLineView } from "../components/ui-bits";
import BookMeeting from "../components/BookMeeting";
import { useLang } from "../i18n/LangContext";

const STATS = {
  en: [
    { icon: Percent, t: "8%", d: "annual interest" },
    { icon: ShieldCheck, t: "Senior-secured", d: "real estate collateral" },
    { icon: CalendarDays, t: "6 – 24 months", d: "typical loan term" },
    { icon: MapPin, t: "Portugal", d: "residential focus" },
  ],
  pt: [
    { icon: Percent, t: "8%", d: "juro anual" },
    { icon: ShieldCheck, t: "Garantia sénior", d: "colateral imobiliário" },
    { icon: CalendarDays, t: "6 – 24 meses", d: "prazo típico do crédito" },
    { icon: MapPin, t: "Portugal", d: "foco residencial" },
  ],
  nl: [
    { icon: Percent, t: "8%", d: "jaarrente" },
    { icon: ShieldCheck, t: "Senior zekerheid", d: "vastgoedonderpand" },
    { icon: CalendarDays, t: "6 – 24 maanden", d: "typische looptijd" },
    { icon: MapPin, t: "Portugal", d: "residentiële focus" },
  ],
};

const APPROACH = {
  en: [
    { icon: FileText, t: "Experienced developers", d: "We work with established developers with a proven track record in residential projects." },
    { icon: ShieldCheck, t: "Strong security", d: "Loans are secured on high-quality real estate assets, with first-ranking mortgages — and Verde invests its own capital in every facility." },
    { icon: Layers, t: "Disciplined underwriting", d: "Independent valuations, local market expertise and clear exit routes." },
  ],
  pt: [
    { icon: FileText, t: "Promotores experientes", d: "Trabalhamos com promotores estabelecidos, com historial comprovado em projetos residenciais." },
    { icon: ShieldCheck, t: "Garantias sólidas", d: "Os créditos são garantidos por imóveis de elevada qualidade, com hipotecas de primeiro grau — e a Verde investe o seu próprio capital em cada operação." },
    { icon: Layers, t: "Subscrição disciplinada", d: "Avaliações independentes, conhecimento local do mercado e vias de saída claras." },
  ],
  nl: [
    { icon: FileText, t: "Ervaren ontwikkelaars", d: "Wij werken met gevestigde ontwikkelaars met een bewezen staat van dienst in residentiële projecten." },
    { icon: ShieldCheck, t: "Sterke zekerheden", d: "Leningen zijn gedekt op hoogwaardig vastgoed met eerste rangs hypotheken — en Verde belegt eigen kapitaal in elk krediet." },
    { icon: Layers, t: "Gedisciplineerde beoordeling", d: "Onafhankelijke taxaties, lokale marktkennis en heldere exit-routes." },
  ],
};

const JOURNEY = {
  en: [
    { img: "/stage-acquire.jpg", t: "Land acquisition", d: "Finance the purchase of pre-development land with strong fundamentals." },
    { img: "/stage-unlock.jpg", t: "Licensing", d: "Fund planning, permits and related costs to de-risk the project and increase its value." },
    { img: "/stage-bridge.jpg", t: "Pre-construction bridge", d: "Provide capital until bank construction finance is available or the project is sold." },
    { img: "/stage-coast.jpg", t: "Bank financing or sale", d: "Repayment of the loan, typically within 6 – 24 months." },
  ],
  pt: [
    { img: "/stage-acquire.jpg", t: "Aquisição de terreno", d: "Financie a compra de terreno pré-desenvolvimento com fundamentos sólidos." },
    { img: "/stage-unlock.jpg", t: "Licenciamento", d: "Financie planeamento, licenças e custos associados para reduzir o risco do projeto e aumentar o seu valor." },
    { img: "/stage-bridge.jpg", t: "Ponte à pré-construção", d: "Fornecemos capital até que o financiamento bancário à construção esteja disponível ou o projeto seja vendido." },
    { img: "/stage-coast.jpg", t: "Financiamento bancário ou venda", d: "Reembolso do crédito, tipicamente em 6 – 24 meses." },
  ],
  nl: [
    { img: "/stage-acquire.jpg", t: "Grondverwerving", d: "Financier de aankoop van grond met sterke fundamenten." },
    { img: "/stage-unlock.jpg", t: "Vergunningen", d: "Financier bestemmingsplannen, vergunningen en bijbehorende kosten — verlaag het risico en verhoog de waarde." },
    { img: "/stage-bridge.jpg", t: "Voorbouwfase-overbrugging", d: "Kapitaal tot de bankbouwfinanciering beschikbaar is of het project wordt verkocht." },
    { img: "/stage-coast.jpg", t: "Bankfinanciering of verkoop", d: "Afbetaling van de lening, doorgaans binnen 6 – 24 maanden." },
  ],
};

const TERMS = {
  en: [
    { k: "Target annual interest", v: "8%" },
    { k: "Paid", v: "Quarterly" },
    { k: "Typical loan term", v: "6 – 24 months" },
    { k: "Minimum investment", v: "€100,000" },
    { k: "Security", v: "First-ranking mortgage over the asset" },
    { k: "Repayment", v: "Bank refinancing, sale or pre-sales" },
    { k: "Fees", v: "As per facility terms" },
  ],
  pt: [
    { k: "Juro anual alvo", v: "8%" },
    { k: "Pagamento", v: "Trimestral" },
    { k: "Prazo típico do crédito", v: "6 – 24 meses" },
    { k: "Investimento mínimo", v: "€100.000" },
    { k: "Garantia", v: "Hipoteca de primeiro grau sobre o ativo" },
    { k: "Reembolso", v: "Refinanciamento bancário, venda ou pré-vendas" },
    { k: "Comissões", v: "Conforme as condições da operação" },
  ],
  nl: [
    { k: "Streefrente per jaar", v: "8%" },
    { k: "Uitbetaling", v: "Per kwartaal" },
    { k: "Typische looptijd", v: "6 – 24 maanden" },
    { k: "Minimale investering", v: "€100.000" },
    { k: "Zekerheid", v: "Eerste rangs hypotheek op het actief" },
    { k: "Terugbetaling", v: "Bankherfinanciering, verkoop of voorverkoop" },
    { k: "Kosten", v: "Volgens de kredietvoorwaarden" },
  ],
};

const FAQS = {
  en: [
    { q: "How does the 8% yield get paid?", a: "Quarterly, directly to your designated bank account, for the life of the loan." },
    { q: "What is my capital secured by?", a: "A first-ranking mortgage over the underlying property — and Verde Capital invests its own capital alongside yours in every facility." },
    { q: "What are the typical loan terms?", a: "6 – 24 months, with a defined repayment route: bank refinancing, sale or pre-sales." },
    { q: "What types of projects do you finance?", a: "Residential living categories across Portugal: homes and apartments for sale, build to rent, student housing, senior housing, aparthotels, co-living, serviced apartments and hotels." },
    { q: "Who can invest?", a: "Professional and qualified investors — private individuals, family offices and companies — from a €100,000 ticket, subject to onboarding and suitability checks." },
    { q: "What fees apply?", a: "Fees are set out in each facility's terms before you commit. There is no charge to request information or speak with the team." },
    { q: "What are the main risks?", a: "Development lending carries real risks: delays, licensing, market movements. Capital is at risk, and past performance is not a guide to future returns. Our mitigants are senior-secured positions, first-ranking mortgages, low loan-to-value lending and short terms." },
    { q: "How is the loan underwritten?", a: "Verde, our AI underwriter, organises and analyses each case; our credit team and verified local experts test it. Nothing is financed without full due diligence." },
    { q: "How do you select developers?", a: "We work with established developers with a proven track record in Portuguese residential projects." },
    { q: "What is the typical loan-to-value (LTV)?", a: "We apply a low loan-to-value on every facility. Specific ratios are confirmed during underwriting for each project." },
    { q: "How do I get started?", a: "Request information below, or call us. We will send you the current offer and full details." },
    { q: "Can I schedule a meeting with the team?", a: "Yes. Call or WhatsApp us and we will arrange a call at a time that suits you." },
  ],
  pt: [
    { q: "Como é pago o rendimento de 8%?", a: "Trimestralmente, diretamente para a conta bancária por si indicada, durante a vigência do crédito." },
    { q: "O que garante o meu capital?", a: "Uma hipoteca de primeiro grau sobre o imóvel subjacente — e a Verde Capital investe o seu próprio capital juntamente com o seu em cada operação." },
    { q: "Quais são os prazos típicos do crédito?", a: "6 – 24 meses, com uma via de reembolso definida: refinanciamento bancário, venda ou pré-vendas." },
    { q: "Que tipos de projetos financiam?", a: "Categorias habitacionais por todo o Portugal: habitação e apartamentos para venda, arrendamento institucional (build to rent), habitação estudantil, habitação sénior, aparthotéis, co-living, apartamentos de serviços e hotéis." },
    { q: "Quem pode investir?", a: "Investidores profissionais e qualificados — particulares, family offices e empresas — a partir de €100.000, sujeito a onboarding e verificação de adequação." },
    { q: "Que comissões se aplicam?", a: "As comissões estão definidas nas condições de cada operação antes de qualquer compromisso. Não há qualquer custo por pedir informação ou falar com a equipa." },
    { q: "Quais são os principais riscos?", a: "O crédito ao desenvolvimento implica riscos reais: atrasos, licenciamento, oscilações de mercado. O capital está em risco e desempenhos passados não garantem retornos futuros. Os nossos mitigantes são posições com garantia sénior, hipotecas de primeiro grau, rácios empréstimo-valor baixos e prazos curtos." },
    { q: "Como é que o crédito é subscrito?", a: "A Verde, a nossa subscritora de IA, organiza e analisa cada processo; a nossa equipa de crédito e especialistas locais verificados testam-no. Nada é financiado sem due diligence completa." },
    { q: "Como selecionam os promotores?", a: "Trabalhamos com promotores estabelecidos, com historial comprovado em projetos residenciais em Portugal." },
    { q: "Qual é o rácio empréstimo-valor (LTV) típico?", a: "Aplicamos um rácio empréstimo-valor baixo em cada operação. Os rácios específicos são confirmados durante a subscrição de cada projeto." },
    { q: "Como posso começar?", a: "Peça informação abaixo ou ligue-nos. Enviaremos a oferta atual e todos os detalhes." },
    { q: "Posso agendar uma reunião com a equipa?", a: "Sim. Ligue ou envie WhatsApp e marcamos uma chamada à hora que lhe for mais conveniente." },
  ],
  nl: [
    { q: "Hoe wordt het rendement van 8% uitbetaald?", a: "Per kwartaal, rechtstreeks naar de bankrekening die u opgeeft, gedurende de looptijd van de lening." },
    { q: "Waarop is mijn kapitaal gedekt?", a: "Een eerste rangs hypotheek op de onderliggende woning — en Verde Capital belegt eigen kapitaal mee in elk krediet." },
    { q: "Wat zijn de typische looptijden?", a: "6 – 24 maanden, met een duidelijke aflossingsroute: bankherfinanciering, verkoop of voorverkoop." },
    { q: "Welke soorten projecten financieren jullie?", a: "Wooncategorieën in heel Portugal: woningen en appartementen voor verkoop, build to rent, studentenhuisvesting, seniorenwoningen, aparthotels, co-living, serviced apartments en hotels." },
    { q: "Wie kan er investeren?", a: "Professionele en gekwalificeerde beleggers — particulieren, family offices en bedrijven — vanaf een ticket van €100.000, onder voorbehoud van onboarding en geschiktheidstoets." },
    { q: "Welke kosten gelden er?", a: "De kosten staan in de voorwaarden van elk krediet voordat u zich verbindt. Informatie aanvragen of met het team praten is gratis." },
    { q: "Wat zijn de grootste risico's?", a: "Ontwikkelingskrediet kent reële risico's: vertraging, vergunningen en marktbewegingen. Uw kapitaal staat op het spel en prestaties uit het verleden garanderen niets voor de toekomst. Onze beheersmaatregelen: senior gedekte posities, eerste rangs hypotheken, lage loan-to-value en korte looptijden." },
    { q: "Hoe wordt de lening beoordeeld?", a: "Verde, onze AI-underwriter, ordent en analyseert elk dossier; ons kredietteam en geverifieerde lokale experts toetsen het. Niets wordt gefinancierd zonder volledige due diligence." },
    { q: "Hoe selecteren jullie ontwikkelaars?", a: "Wij werken met gevestigde ontwikkelaars met een bewezen staat van dienst in Portugese woningbouwprojecten." },
    { q: "Wat is de typische loan-to-value (LTV)?", a: "Wij hanteren een lage loan-to-value bij elk krediet. De specifieke ratio's worden per project tijdens de beoordeling vastgesteld." },
    { q: "Hoe begin ik?", a: "Vraag hieronder informatie aan of bel ons. Wij sturen u het huidige aanbod en alle details." },
    { q: "Kan ik een gesprek met het team plannen?", a: "Ja. Bel of app ons en wij plannen een gesprek op een moment dat u schikt." },
  ],
};

const TICKETS = {
  en: ["€100–250k", "€250–500k", "€500k–1m", "€1m+", "not yet determined"],
  pt: ["€100–250 mil", "€250–500 mil", "€500 mil–1M", "€1M+", "ainda não determinado"],
  nl: ["€100–250k", "€250–500k", "€500k–1 mln", "€1 mln+", "nog niet bepaald"],
};
const INVESTOR_TYPES = {
  en: ["Family office", "Private investor", "Entrepreneur investing through a company", "Adviser", "Other"],
  pt: ["Family office", "Investidor particular", "Empresário a investir através de empresa", "Consultor", "Outro"],
  nl: ["Family office", "Particuliere belegger", "Ondernemer die via een vennootschap belegt", "Adviseur", "Anders"],
};

const eur = new Intl.NumberFormat("en-GB", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

const field =
  "w-full rounded-lg border border-sand bg-white px-4 py-3 text-sm outline-none transition-colors placeholder:text-fog/60 focus:border-verde-700";
const label = "mb-1.5 block text-xs font-medium text-ink";

function HeroCalculator() {
  const { lang } = useLang();
  const T = {
    en: {
      title: "Illustrative investment", annual: "Annual interest", at: "at 8%",
      quarterly: "Quarterly payment", paid: "paid to your account",
      monthly: "Monthly equivalent", compare: "for comparison only",
      note: "Illustrative only. Not a payment schedule or guarantee. Actual distributions, fees and repayment rights depend on final facility terms. Capital is at risk.",
    },
    pt: {
      title: "Investimento ilustrativo", annual: "Juro anual", at: "a 8%",
      quarterly: "Pagamento trimestral", paid: "pago para a sua conta",
      monthly: "Equivalente mensal", compare: "apenas para comparação",
      note: "Apenas ilustrativo. Não é um calendário de pagamentos nem uma garantia. As distribuições, comissões e direitos de reembolso reais dependem das condições finais da operação. O capital está em risco.",
    },
    nl: {
      title: "Illustratieve investering", annual: "Jaarrente", at: "tegen 8%",
      quarterly: "Kwartaaluitbetaling", paid: "uitgekeerd op uw rekening",
      monthly: "Maandelijkse equivalent", compare: "alleen ter vergelijking",
      note: "Alleen ter illustratie. Geen betalingsschema of garantie. Werkelijke uitkeringen, kosten en aflossingsrechten hangen af van de definitieve kredietvoorwaarden. Uw kapitaal staat op het spel.",
    },
  }[lang];
  const [amount, setAmount] = useState(250000);
  const annual = (amount * 0.08);
  const quarterly = annual / 4;
  const monthly = annual / 12;

  return (
    <div className="rounded-2xl bg-cream p-6 sm:p-8 shadow-[0_32px_64px_-24px_rgba(0,0,0,0.5)]" data-testid="calculator-card">
      <p className="text-xs font-medium text-fog">{T.title}</p>
      <p className="mt-1 font-display text-4xl font-black tracking-tight text-charcoal" data-testid="calculator-amount">
        {eur.format(amount)}
      </p>
      <input
        id="calc-amount"
        type="range"
        min={100000}
        max={1000000}
        step={50000}
        value={amount}
        onChange={(e) => setAmount(Number(e.target.value))}
        className="mt-5 h-2 w-full cursor-pointer appearance-none rounded-full bg-sand accent-[#114D38]"
        data-testid="calculator-slider"
        aria-label="Investment amount"
      />
      <div className="flex items-center justify-between text-[11px] font-medium text-fog">
        <span>€100k</span>
        <span>€1,000k</span>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 border-t border-sand pt-6">
        <div>
          <p className="text-[11px] font-medium text-fog">{T.annual}</p>
          <p className="mt-1 font-display text-xl font-black text-charcoal" data-testid="calculator-annual">
            {eur.format(annual)}
          </p>
          <p className="text-[10px] text-fog">{T.at}</p>
        </div>
        <div>
          <p className="text-[11px] font-medium text-fog">{T.quarterly}</p>
          <p className="mt-1 font-display text-xl font-black text-charcoal" data-testid="calculator-quarterly">
            {eur.format(quarterly)}
          </p>
          <p className="text-[10px] text-fog">{T.paid}</p>
        </div>
        <div>
          <p className="text-[11px] font-medium text-fog">{T.monthly}</p>
          <p className="mt-1 font-display text-xl font-black text-charcoal" data-testid="calculator-monthly">
            {eur.format(monthly)}
          </p>
          <p className="text-[10px] text-fog">{T.compare}</p>
        </div>
      </div>

      <p className="mt-6 border-t border-sand pt-4 text-[10px] leading-relaxed text-fog" data-testid="calculator-note">
        {T.note}
      </p>
    </div>
  );
}

function FaqItem({ q, a, i }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-sand" data-testid={`faq-item-${i}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 py-5 text-left"
        data-testid={`faq-question-${i}`}
      >
        <span className="text-[15px] font-semibold text-charcoal">{q}</span>
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-sand text-verde-700 transition-transform duration-300 ${open ? "rotate-45" : ""}`}
          aria-hidden="true"
        >
          <span className="relative block h-3 w-3">
            <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-current" />
            <span className="absolute top-1/2 left-0 h-px w-full -translate-y-1/2 bg-current" />
          </span>
        </span>
      </button>
      {open && (
        <p className="pb-5 pr-8 text-sm leading-relaxed text-ink" data-testid={`faq-answer-${i}`}>
          {a}
        </p>
      )}
    </div>
  );
}

function InvestorContact() {
  const { lang, lp } = useLang();
  const T = {
    en: {
      eyebrow: "Investor conversation",
      h1: "Let's discuss", h2: "your investment.",
      copy: "Schedule a call with our investor relations team to discuss the strategy, current pipeline and terms.",
      team: "Verde Capital investor relations", line: "One direct line. No call centres.",
      book: "Book a meeting", info: "Request investor information",
      alt: "Terrace overlooking the Portuguese coast at dusk",
    },
    pt: {
      eyebrow: "Conversa com investidores",
      h1: "Vamos falar do", h2: "seu investimento.",
      copy: "Agende uma chamada com a nossa equipa de relações com investidores para falar da estratégia, do pipeline atual e das condições.",
      team: "Relações com investidores da Verde Capital", line: "Uma linha direta. Sem call centres.",
      book: "Agendar reunião", info: "Pedir informação para investidores",
      alt: "Varanda sobre a costa portuguesa ao anoitecer",
    },
    nl: {
      eyebrow: "Gesprek met investeerders",
      h1: "Laten we uw", h2: "investering bespreken.",
      copy: "Plan een gesprek met ons investorrelatieteam over de strategie, de huidige pijplijn en de voorwaarden.",
      team: "Investorrelaties van Verde Capital", line: "Eén directe lijn. Geen callcentres.",
      book: "Gesprek plannen", info: "Investeerdersinformatie aanvragen",
      alt: "Terras met uitzicht op de Portugese kust in de avond",
    },
  }[lang];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
      <Reveal className="lg:col-span-7">
        <Eyebrow light>{T.eyebrow}</Eyebrow>
        <h2 className="display mt-5 text-3xl sm:text-4xl lg:text-[44px] text-white" data-testid="investors-contact-headline">
          <MaskLineView>{T.h1}</MaskLineView>
          <MaskLineView delay={0.1}>{T.h2}</MaskLineView>
        </h2>
        <Reveal delay={0.2} className="mt-5 max-w-lg">
          <p className="text-[15px] leading-relaxed text-cream/75">
            {T.copy}
          </p>
        </Reveal>
        <Reveal delay={0.25} className="mt-6 flex items-center gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-brass/60 bg-white/5 font-display text-lg font-black text-brass">
            VC
          </span>
          <div>
            <p className="text-sm font-semibold text-white">{T.team}</p>
            <p className="text-xs text-cream/60">{T.line}</p>
          </div>
        </Reveal>
        <Reveal delay={0.3} className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
          <BookMeeting label={T.book} variant="primary" testid="contact-book-meeting" />
          <Link
            to={lp("/investor-info")}
            data-testid="contact-info-cta"
            className="inline-flex items-center justify-center gap-2.5 rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-white/10"
          >
            {T.info}
          </Link>
        </Reveal>
      </Reveal>
      <Reveal delay={0.2} className="lg:col-span-5">
        <figure className="overflow-hidden rounded-2xl border border-white/10" data-testid="investors-conversation-figure">
          <img
            src="/investors-conversation.jpg"
            alt={T.alt}
            className="h-72 w-full object-cover lg:h-96"
          />
        </figure>
      </Reveal>
    </div>
  );
}

export default function Investors() {
  const { lang, lp } = useLang();
  const T = {
    en: {
      eyebrow: "For investors",
      h1a: "8% annual yield.", h1b: "Secured by real assets.",
      heroCopy: "Invest in short-term, senior-secured loans to experienced real estate developers, financing land acquisition, licensing and pre-construction projects in Portugal.",
      infoCta: "Request investor information", schedule: "Schedule a meeting",
      oppEyebrow: "The opportunity",
      oppH1: "Portugal needs more homes.", oppH2: "Developers need capital.",
      oppCopy: "Portugal has a significant housing shortage. Strong demand, limited new supply and planning complexity prevent many viable projects from reaching construction. We provide the capital for the critical early stages where experienced developers need funding before banks step in.",
      oppLink: "Read more about the opportunity",
      oppNumber: "~300,000", oppNumberLabel: "homes missing in Portugal",
      oppCard: "Estimated housing construction shortfall relative to household growth over the past decade.",
      oppSource: "Source: Banco de Portugal, 2026 reporting on the housing construction gap.",
      apprEyebrow: "Our approach",
      apprH1: "We finance what", apprH2a: "banks don't ", apprH2b: "– yet.",
      apprCopy: "We provide short-term, senior-secured loans to experienced developers, filling the gap between land acquisition, licensing, and bank construction financing.",
      journEyebrow: "The development journey",
      journH1: "We finance the critical", journH2: "early stages.",
      retEyebrow: "Returns and terms",
      retH1: "A clear,", retH2: "predictable return.",
      retCopy: "We target an 8% annual interest rate, with typical loan terms of 6 – 24 months. Returns are generated from senior-secured, short-term loans backed by real estate collateral.",
      retLink: "View detailed terms",
      faqEyebrow: "Frequently asked questions", faqH: "Key questions, clear answers.",
      formEyebrow: "Request investor information", formH: "Talk to our team.",
      done: "Request received.", doneCopy1: "Verde Capital will be in touch shortly. A confirmation has been sent to ",
      name: "Name *", email: "Email *", country: "Country *", itype: "Investor type *",
      itypePh: "Select investor type", ticket: "Indicative ticket *", ticketPh: "Select ticket",
      message: "Message",
      consent: "I consent to Verde Capital contacting me about investor opportunities.",
      err: "Something went wrong. Please try again.",
      sending: "Sending…", submit: "Request information",
      oppAlt: "Coastal town in Portugal", heroAlt: "Coastal Portuguese development site at dusk",
    },
    pt: {
      eyebrow: "Para investidores",
      h1a: "Rentabilidade de 8%.", h1b: "Garantido por ativos reais.",
      heroCopy: "Invista em créditos de curto prazo com garantia sénior a promotores imobiliários experientes, financiando a aquisição de terreno, o licenciamento e projetos de pré-construção em Portugal.",
      infoCta: "Pedir informação para investidores", schedule: "Agendar reunião",
      oppEyebrow: "A oportunidade",
      oppH1: "Portugal precisa de mais habitação.", oppH2: "Os promotores precisam de capital.",
      oppCopy: "Portugal enfrenta uma escassez significativa de habitação. Procura forte, oferta nova limitada e a complexidade do licenciamento impedem que muitos projetos viáveis cheguem à construção. Fornecemos o capital para as fases iniciais críticas, em que promotores experientes precisam de financiamento antes da entrada dos bancos.",
      oppLink: "Saber mais sobre a oportunidade",
      oppNumber: "~300.000", oppNumberLabel: "habitações em falta em Portugal",
      oppCard: "Falta estimada de construção de habitação face ao crescimento dos agregados familiares na última década.",
      oppSource: "Fonte: Banco de Portugal, relatório de 2026 sobre o défice de construção de habitação.",
      apprEyebrow: "A nossa abordagem",
      apprH1: "Financiamos o que", apprH2a: "os bancos ", apprH2b: "ainda não financiam.",
      apprCopy: "Concedemos créditos de curto prazo com garantia sénior a promotores experientes, colmatando a distância entre a aquisição de terreno, o licenciamento e o financiamento bancário à construção.",
      journEyebrow: "O percurso do desenvolvimento",
      journH1: "Financiamos as fases", journH2: "iniciais críticas.",
      retEyebrow: "Retornos e condições",
      retH1: "Um retorno claro,", retH2: "previsível.",
      retCopy: "Visamos uma taxa de juro anual de 8%, com prazos típicos de 6 – 24 meses. Os retornos provêm de créditos de curto prazo com garantia sénior sobre colateral imobiliário.",
      retLink: "Ver condições detalhadas",
      faqEyebrow: "Perguntas frequentes", faqH: "Perguntas-chave, respostas claras.",
      formEyebrow: "Pedir informação para investidores", formH: "Fale com a nossa equipa.",
      done: "Pedido recebido.", doneCopy1: "A Verde Capital entrará em contacto em breve. Foi enviada uma confirmação para ",
      name: "Nome *", email: "Email *", country: "País *", itype: "Tipo de investidor *",
      itypePh: "Selecione o tipo de investidor", ticket: "Ticket indicativo *", ticketPh: "Selecione o ticket",
      message: "Mensagem",
      consent: "Autorizo a Verde Capital a contactar-me sobre oportunidades de investimento.",
      err: "Algo falhou. Tente novamente.",
      sending: "A enviar…", submit: "Pedir informação",
      oppAlt: "Vila costeira em Portugal", heroAlt: "Estaleiro de desenvolvimento costeiro em Portugal ao anoitecer",
    },
    nl: {
      eyebrow: "Voor investeerders",
      h1a: "8% jaarrendement.", h1b: "Gedekt door echte activa.",
      heroCopy: "Beleg in kortlopende, senior gedekte leningen aan ervaren vastgoedontwikkelaars die grondverwerving, vergunningen en voorbouwfase-projecten in Portugal financieren.",
      infoCta: "Investeerdersinformatie aanvragen", schedule: "Gesprek plannen",
      oppEyebrow: "De kans",
      oppH1: "Portugal heeft meer woningen nodig.", oppH2: "Ontwikkelaars hebben kapitaal nodig.",
      oppCopy: "Portugal kent een groot woningtekort. Sterke vraag, beperkt nieuw aanbod en planologische complexiteit houden veel levensvatbare projecten van de bouwfase af. Wij financieren de kritieke vroege fasen waarin ervaren ontwikkelaars kapitaal nodig hebben vóórdat banken instappen.",
      oppLink: "Lees meer over de kans",
      oppNumber: "~300.000", oppNumberLabel: "woningen tekort in Portugal",
      oppCard: "Geschat bouwtekort voor woningen ten opzichte van de huishoudensgroei in het afgelopen decennium.",
      oppSource: "Bron: Banco de Portugal, rapportage 2026 over het woningtekort.",
      apprEyebrow: "Onze aanpak",
      apprH1: "Wij financieren wat", apprH2a: "banken ", apprH2b: "nog niet financieren.",
      apprCopy: "Wij verstrekken kortlopende, senior gedekte leningen aan ervaren ontwikkelaars en dichten de kloof tussen grondverwerving, vergunningen en bankbouwfinanciering.",
      journEyebrow: "De ontwikkelingsreis",
      journH1: "Wij financieren de kritieke", journH2: "vroege fasen.",
      retEyebrow: "Rendement en voorwaarden",
      retH1: "Een helder,", retH2: "voorspelbaar rendement.",
      retCopy: "Wij streven naar 8% jaarrente, met typische looptijden van 6 – 24 maanden. Het rendement komt uit kortlopende, senior gedekte leningen met vastgoed als onderpand.",
      retLink: "Bekijk de gedetailleerde voorwaarden",
      faqEyebrow: "Veelgestelde vragen", faqH: "Kernvragen, heldere antwoorden.",
      formEyebrow: "Investeerdersinformatie aanvragen", formH: "Praat met ons team.",
      done: "Aanvraag ontvangen.", doneCopy1: "Verde Capital neemt zo snel mogelijk contact op. Een bevestiging is verzonden naar ",
      name: "Naam *", email: "E-mail *", country: "Land *", itype: "Type belegger *",
      itypePh: "Kies een type belegger", ticket: "Indicatief ticket *", ticketPh: "Kies een ticket",
      message: "Bericht",
      consent: "Ik geef Verde Capital toestemming om mij te benaderen over beleggingskansen.",
      err: "Er ging iets mis. Probeer het opnieuw.",
      sending: "Versturen…", submit: "Informatie aanvragen",
      oppAlt: "Kustdorp in Portugal", heroAlt: "Kustontwikkelingslocatie in Portugal in de avond",
    },
  }[lang];

  const faqs = FAQS[lang];
  const [form, setForm] = useState({ name: "", email: "", country: "", investor_type: "", ticket: "", message: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.name.trim() && /.+@.+\..+/.test(form.email) && form.country.trim() && form.investor_type && form.ticket && form.consent;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await post("/investor-requests", { ...form, lang, ...leadSourceFields() });
      measureLead();
      setDone(true);
      window.scrollTo(0, 0);
    } catch {
      setError(T.err);
    }
    setSubmitting(false);
  };

  return (
    <div className="bg-cream">
      {/* ── HERO (DARK) ──────────────────────────────────── */}
      <section className="relative overflow-hidden bg-verde-950" data-testid="investors-hero">
        <img
          src="/investors-hero.jpg"
          alt={T.heroAlt}
          className="absolute inset-0 h-full w-full object-cover opacity-40"
          data-testid="investors-hero-photo"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-verde-950 via-verde-950/80 to-verde-950/40" />
        <div className="container-x relative pt-28 sm:pt-40 pb-14 sm:pb-20 lg:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:items-center">
            <div className="lg:col-span-7">
              <Eyebrow light>{T.eyebrow}</Eyebrow>
              <h1 className="display mt-6 text-[2.6rem] sm:text-5xl lg:text-[56px]" data-testid="page-headline">
                <MaskLineView>
                  <span className="text-mint">{T.h1a}</span>
                </MaskLineView>
                <MaskLineView delay={0.12}>
                  <span className="text-white">{T.h1b}</span>
                </MaskLineView>
              </h1>
              <Reveal delay={0.25} className="mt-7 max-w-xl">
                <p className="text-lg leading-relaxed text-cream/85">
                  {T.heroCopy}
                </p>
              </Reveal>
              <Reveal delay={0.35} className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
                <Link
                  to={lp("/investor-info")}
                  data-testid="hero-primary-cta"
                  className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-mint px-7 py-3.5 text-sm font-semibold text-verde-950 transition-colors duration-300 hover:bg-white"
                >
                  {T.infoCta}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <BookMeeting label={T.schedule} variant="ghost-light" testid="hero-secondary-cta" />
              </Reveal>
            </div>
            <Reveal delay={0.3} className="lg:col-span-5">
              <HeroCalculator />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── STATS STRIP (DARK) ───────────────────────────── */}
      <section className="bg-verde-900 border-y border-white/10" data-testid="investors-stats">
        <div className="container-x grid grid-cols-2 gap-y-6 py-8 sm:grid-cols-4 lg:divide-x lg:divide-white/10">
          {STATS[lang].map((s) => (
            <div key={s.t} className="flex items-center gap-4 px-3 lg:px-8">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/5 text-mint">
                <s.icon className="h-5 w-5" strokeWidth={1.6} aria-hidden="true" />
              </span>
              <div>
                <p className="font-display text-base font-bold text-white">{s.t}</p>
                <p className="text-xs text-cream/60">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── THE OPPORTUNITY ──────────────────────────────── */}
      <section className="py-20 lg:py-28" data-testid="investors-opportunity">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:items-center">
          <div className="lg:col-span-6">
            <Eyebrow>{T.oppEyebrow}</Eyebrow>
            <h2 className="display mt-5 text-3xl sm:text-4xl text-charcoal" data-testid="opportunity-headline">
              <MaskLineView>{T.oppH1}</MaskLineView>
              <MaskLineView delay={0.1}>
                <span className="text-verde-700">{T.oppH2}</span>
              </MaskLineView>
            </h2>
            <Reveal delay={0.2} className="mt-6 max-w-lg">
              <p className="text-[15px] leading-relaxed text-fog">
                {T.oppCopy}
              </p>
            </Reveal>
            <Reveal delay={0.3} className="mt-8">
              <a
                href={lp("/products/pre-construction-finance")}
                data-testid="opportunity-link"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-verde-800 transition-colors hover:text-verde-700"
              >
                {T.oppLink}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-5 items-stretch" data-testid="opportunity-card">
                <img
                  src="/stage-coast.jpg"
                  alt={T.oppAlt}
                  className="h-64 w-full rounded-2xl object-cover sm:h-full"
                />
                <div className="flex flex-col justify-between rounded-2xl bg-white border border-sand p-6">
                  <div>
                    <p className="font-display text-4xl font-black tracking-tight text-charcoal">{T.oppNumber}</p>
                    <p className="mt-1 font-display text-lg font-bold text-verde-700">{T.oppNumberLabel}</p>
                    <p className="mt-4 text-xs leading-relaxed text-fog">
                      {T.oppCard}
                    </p>
                  </div>
                  <p className="mt-6 border-t border-sand pt-3 text-[10px] leading-relaxed text-fog/80">
                    {T.oppSource}
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── OUR APPROACH ─────────────────────────────────── */}
      <section className="bg-white border-y border-sand py-20 lg:py-28" data-testid="investors-approach">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:items-center">
          <div className="lg:col-span-5">
            <Eyebrow>{T.apprEyebrow}</Eyebrow>
            <h2 className="display mt-5 text-3xl sm:text-4xl text-charcoal" data-testid="approach-headline">
              <MaskLineView>{T.apprH1}</MaskLineView>
              <MaskLineView delay={0.1}>
                {T.apprH2a}<span className="text-verde-700">{T.apprH2b}</span>
              </MaskLineView>
            </h2>
            <Reveal delay={0.2} className="mt-6 max-w-md">
              <p className="text-[15px] leading-relaxed text-fog">
                {T.apprCopy}
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-2">
            {APPROACH[lang].map((a, i) => (
              <Reveal key={a.t} delay={0.1 + i * 0.08}>
                <div
                  className="flex items-start gap-5 rounded-2xl p-6 transition-colors duration-300 hover:bg-cream/60"
                  data-testid={`investors-approach-${i}`}
                >
                  <span className="relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-[#EAF3EE] via-[#E0EEE6] to-[#CFE5D9] ring-1 ring-verde-800/10 shadow-[0_12px_26px_-16px_rgba(7,39,29,0.45)]">
                    <span className="absolute inset-0 bg-gradient-to-b from-white/70 via-transparent to-transparent" aria-hidden="true" />
                    <a.icon className="relative h-5 w-5 text-verde-800" strokeWidth={1.7} aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="font-display text-lg font-bold text-charcoal">{a.t}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-fog">{a.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── THE DEVELOPMENT JOURNEY ──────────────────────── */}
      <section className="py-20 lg:py-28" data-testid="investors-journey">
        <div className="container-x">
          <div className="max-w-2xl">
            <Eyebrow>{T.journEyebrow}</Eyebrow>
            <h2 className="display mt-5 text-3xl sm:text-4xl text-charcoal" data-testid="journey-headline">
              <MaskLineView>{T.journH1}</MaskLineView>
              <MaskLineView delay={0.1}>{T.journH2}</MaskLineView>
            </h2>
          </div>
          <div className="mt-12 flex flex-col gap-6 sm:grid sm:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] sm:gap-3">
            {JOURNEY[lang].map((s, i) => (
              <div key={s.t} className="contents">
                <Reveal delay={0.06 + i * 0.07}>
                  <div className="h-full" data-testid={`investors-step-${i}`}>
                    <div className="relative">
                      <img loading="lazy" decoding="async" src={s.img} alt={s.t} className="h-28 w-full rounded-xl object-cover" />
                      <span className="absolute -top-3 -left-3 flex h-7 w-7 items-center justify-center rounded-full bg-verde-800 font-mono text-[11px] font-bold text-mint">
                        {i + 1}
                      </span>
                    </div>
                    <h3 className="mt-4 font-display text-base font-bold text-charcoal">{s.t}</h3>
                    <p className="mt-1.5 text-[13px] leading-relaxed text-fog">{s.d}</p>
                  </div>
                </Reveal>
                {i < JOURNEY[lang].length - 1 && (
                  <div className="flex justify-center py-1 sm:mt-12 sm:items-center" aria-hidden="true">
                    <ArrowRight className="h-5 w-5 rotate-90 text-verde-700/50 sm:rotate-0" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── RETURNS AND TERMS ────────────────────────────── */}
      <section className="pb-20 lg:pb-28" data-testid="investors-returns">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 rounded-2xl border border-sand bg-white p-6 sm:p-10 lg:items-center lg:p-14">
            <div className="lg:col-span-6">
              <Eyebrow>{T.retEyebrow}</Eyebrow>
              <h2 className="display mt-5 text-3xl sm:text-4xl text-charcoal" data-testid="returns-headline">
                <MaskLineView>{T.retH1}</MaskLineView>
                <MaskLineView delay={0.1}>
                  <span className="text-verde-700">{T.retH2}</span>
                </MaskLineView>
              </h2>
              <Reveal delay={0.2} className="mt-6 max-w-md">
                <p className="text-[15px] leading-relaxed text-fog">
                  {T.retCopy}
                </p>
              </Reveal>
              <Reveal delay={0.3} className="mt-8">
                <a
                  href={lp("/lending-disclaimer")}
                  data-testid="returns-terms-link"
                  className="group inline-flex items-center gap-2 text-sm font-semibold text-verde-800 transition-colors hover:text-verde-700"
                >
                  {T.retLink}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </Reveal>
            </div>
            <Reveal delay={0.15} className="lg:col-span-6">
              <div className="overflow-hidden rounded-xl border border-sand" data-testid="returns-table">
                {TERMS[lang].map((r, i) => (
                  <div
                    key={r.k}
                    className={`grid grid-cols-2 gap-4 px-5 py-4 text-sm ${i < TERMS[lang].length - 1 ? "border-b border-sand" : ""}`}
                  >
                    <span className="text-fog">{r.k}</span>
                    <span className="font-medium text-charcoal">{r.v}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── INVESTOR CONVERSATION (DARK) ─────────────────── */}
      <section className="bg-verde-950 py-20 lg:py-28" data-testid="investors-contact">
        <div className="container-x">
          <InvestorContact />
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────── */}
      <section className="bg-white py-20 lg:py-28">
        <div className="container-x">
          <div>
            <Eyebrow>{T.faqEyebrow}</Eyebrow>
            <h2 className="display mt-5 text-3xl sm:text-4xl text-charcoal" data-testid="faq-headline">
              <MaskLineView>{T.faqH}</MaskLineView>
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-x-14 lg:grid-cols-2 lg:items-start">
            <div>
              {faqs.slice(0, 6).map((f, i) => (
                <FaqItem key={f.q} q={f.q} a={f.a} i={i} />
              ))}
            </div>
            <div>
              {faqs.slice(6).map((f, j) => (
                <FaqItem key={f.q} q={f.q} a={f.a} i={j + 6} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FORM ─────────────────────────────────────────── */}
      <section id="invest" className="scroll-mt-24 py-20 lg:py-28">
        <div className="container-x max-w-3xl">
          <Eyebrow>{T.formEyebrow}</Eyebrow>
          <h2 className="display mt-5 text-3xl sm:text-4xl text-charcoal" data-testid="invest-form-headline">
            <MaskLineView>{T.formH}</MaskLineView>
          </h2>
          {done ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-10 rounded-2xl bg-white border border-sand p-10 text-center" data-testid="investors-confirmation">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-verde-800">
                <Check className="h-5 w-5 text-mint" />
              </span>
              <p className="mt-6 font-display text-2xl font-bold text-charcoal">{T.done}</p>
              <p className="mt-3 text-sm text-ink">
                {T.doneCopy1}{form.email}.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="mt-10 rounded-2xl bg-white border border-sand p-6 sm:p-10 space-y-6" data-testid="investors-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={label}>{T.name}</label>
                  <input className={field} value={form.name} onChange={set("name")} data-testid="investors-name" />
                </div>
                <div>
                  <label className={label}>{T.email}</label>
                  <input type="email" className={field} value={form.email} onChange={set("email")} data-testid="investors-email" />
                </div>
                <div>
                  <label className={label}>{T.country}</label>
                  <input className={field} value={form.country} onChange={set("country")} data-testid="investors-country" />
                </div>
                <div>
                  <label className={label}>{T.itype}</label>
                  <select className={field} value={form.investor_type} onChange={set("investor_type")} data-testid="investors-type">
                    <option value="" disabled>{T.itypePh}</option>
                    {INVESTOR_TYPES[lang].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>{T.ticket}</label>
                  <select className={field} value={form.ticket} onChange={set("ticket")} data-testid="investors-ticket">
                    <option value="" disabled>{T.ticketPh}</option>
                    {TICKETS[lang].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={label}>{T.message}</label>
                <textarea rows={4} className={field} value={form.message} onChange={set("message")} data-testid="investors-message" />
              </div>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="investors-consent">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 accent-[#114D38]"
                />
                <span className="text-sm text-ink">
                  {T.consent}
                </span>
              </label>
              {error && <p className="text-sm text-red-700" data-testid="investors-error">{error}</p>}
              <button
                type="submit"
                disabled={!valid || submitting}
                className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-verde-800 px-8 py-4 text-sm font-semibold text-white transition-all hover:bg-verde-700 disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="investors-submit"
              >
                {submitting ? T.sending : T.submit}
                {!submitting && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
              </button>
            </form>
          )}
        </div>
      </section>

    </div>
  );
}
